import { type FC } from 'react';
import { useBlocker } from '@tanstack/react-router';
import { Button, Modal } from '@selfinspectionorg/ui-kit';

interface Props {
  /** When true, navigating away (in-app or full-page) prompts for confirmation. */
  active: boolean;
}

/**
 * Guards navigation away from an in-progress MFA enrollment stage where leaving would destroy work
 * (the created-but-unregistered passkey, or the one-time backup codes). Blocks both in-app router
 * navigation — browser Back included — via a confirmation dialog, and full-page exits — refresh / tab
 * close — via the native beforeunload prompt. It guards the surface, not a route: its hosts (the
 * login wizard on `/login`, the Security page) never navigate while guarded, so every navigation
 * while active is an accidental exit and is blocked unconditionally. Hosts deactivate the guard
 * (`active={false}`) once the guarded work is handed off, so a follow-up full-page redirect is not
 * re-prompted.
 */
export const MfaLeaveGuard: FC<Props> = ({ active }) => {
  const blocker = useBlocker({
    shouldBlockFn: () => true,
    enableBeforeUnload: active,
    disabled: !active,
    withResolver: true,
  });

  if (blocker.status !== 'blocked') {
    return null;
  }

  return (
    <Modal.Wrapper isOpen onClose={blocker.reset} id="mfaLeaveGuardModal" width="400px">
      <Modal.Title
        titleText="Leave two-factor setup?"
        additionalText="Your progress will be lost and you'll need to start over."
      />
      <Modal.ActionButtons onCancelButtonClick={blocker.reset} cancelButtonText="Stay">
        <Button variant="danger" width="132px" onClick={blocker.proceed} data-testid="mfa-leave-confirm-button">
          Leave
        </Button>
      </Modal.ActionButtons>
    </Modal.Wrapper>
  );
};
